import React, { useState } from 'react';
import { Box, Button, List, ListItem, ListItemText } from '@mui/material';
import { IExcludedEmployee } from '../../interfaces/payroll.interface';

const COLLAPSED_ROWS = 5;

/** "Full Name (EMP-001)", falling back to the username, then the id, when the profile has no name or code. */
export function excludedEmployeeLabel(ex: IExcludedEmployee): string {
    const name = ex.fullName?.trim() || (ex.username ?? ex.userId);
    return ex.employeeCode ? `${name} (${ex.employeeCode})` : name;
}

interface Props {
    employees: IExcludedEmployee[];
    reasonLabel: (ex: IExcludedEmployee) => string;
    testId?: string;
}

/**
 * Compact list of employees left out of a payroll run (board DA-03): the first few rows, the rest behind a toggle,
 * and a scroll box when expanded so 30 names never push the run table off the screen.
 */
const ExcludedEmployeesList: React.FC<Props> = ({ employees, reasonLabel, testId }) => {
    const [open, setOpen] = useState(false);
    const shown = open ? employees : employees.slice(0, COLLAPSED_ROWS);
    const hidden = employees.length - COLLAPSED_ROWS;
    return (
        <Box data-testid={testId}>
            <List dense disablePadding sx={open ? { maxHeight: 240, overflowY: 'auto' } : undefined}>
                {shown.map((ex) => (
                    <ListItem key={ex.userId} disableGutters sx={{ py: 0 }}>
                        <ListItemText
                            primary={`${excludedEmployeeLabel(ex)} — ${reasonLabel(ex)}`}
                            sx={{ m: 0, overflowWrap: 'anywhere' }}
                        />
                    </ListItem>
                ))}
            </List>
            {hidden > 0 && (
                <Button size="small" onClick={() => setOpen(o => !o)} sx={{ mt: 0.5, px: 0 }}>
                    {open ? 'Show fewer' : `Show all ${employees.length} (${hidden} more)`}
                </Button>
            )}
        </Box>
    );
};

export default ExcludedEmployeesList;
